import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pluginsApi, requestsApi, autosApi } from './api';
import {
    fetchPlugins, fetchRunFeed, readRun, automationItem, flowItem, requestItem, accessItem,
} from './items';

// THE SECTION'S READS, each one way: { data, error, loading, reload }.
//   loading — a read is in flight (render nothing while there is no data yet)
//   error   — the last read failed (the Error, its `.status` and `.message`);
//             say it with <ErrorEmpty retry={reload}>, never "nothing yet"
//   data    — what came back, kept through a failed reload; null until then
//   missing — an optional read the server does not have yet (an older
//             backend): data is the fallback, and nothing failed
// So a failed load is never mistaken for an empty one.

const MISSING_ROUTE = [404, 405, 501];
/** A route an older server does not have. */
export const isMissingRoute = (e) => MISSING_ROUTE.includes(e?.status);

/**
 * The one read hook: `load()` resolves to the data or throws. It reads again
 * when `deps` change (from nothing, unless `keep`) and on `reload()`; an
 * answer that is no longer the latest asked is dropped. `optional`: a route
 * the server lacks resolves to `fallback`, with `missing` set. `enabled`
 * false reads nothing.
 */
export function useLoad(load, deps, { optional = false, fallback = null, enabled = true, keep = false } = {}) {
    const [state, setState] = useState(() => ({ data: null, error: null, loading: !!enabled, missing: false }));
    const seq = useRef(0);
    const loadRef = useRef(load);
    loadRef.current = load;
    const opts = useRef({ optional, fallback });
    opts.current = { optional, fallback };

    const run = useCallback(() => {
        seq.current += 1;
        const n = seq.current;
        setState((s) => ({ ...s, loading: true }));
        return Promise.resolve()
            .then(() => loadRef.current())
            .then((data) => {
                if (n === seq.current) setState({ data, error: null, loading: false, missing: false });
                return data;
            }, (e) => {
                if (n !== seq.current) return undefined;
                if (opts.current.optional && isMissingRoute(e)) {
                    setState({ data: opts.current.fallback, error: null, loading: false, missing: true });
                } else {
                    setState((s) => ({ data: s.data, error: e || new Error('It could not be read.'), loading: false, missing: false }));
                }
                return undefined;
            });
    }, []);

    useEffect(() => {
        if (!enabled) {
            seq.current += 1;
            setState({ data: null, error: null, loading: false, missing: false });
            return undefined;
        }
        if (!keep) setState({ data: null, error: null, loading: true, missing: false });
        run();
        return () => { seq.current += 1; };
    }, [enabled, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

    return useMemo(() => ({ ...state, reload: run }), [state, run]);
}

const byName = (a, b) => a.name.localeCompare(b.name);
const finder = (list) => (id) => list.find((x) => String(x.id) === String(id)) || null;

/** Every plugin the space can see, in its sets: all, the store (not set up
 *  here), installed, Setting up, Ready, Off (`paused`), Needs you (Setting up,
 *  or a tool that can't run; Off is a hold you chose); and `find(id)`. */
export function usePluginList(groupId) {
    const res = useLoad(() => fetchPlugins(groupId), [groupId]);
    const data = useMemo(() => {
        if (!res.data) return null;
        const all = [...res.data].sort(byName);
        return {
            all,
            store: all.filter((p) => !p.installed),
            installed: all.filter((p) => p.installed),
            settingUp: all.filter((p) => p.settingUp),
            needsYou: all.filter((p) => p.needsYou),
            ready: all.filter((p) => p.installed && !p.settingUp && !p.paused),
            paused: all.filter((p) => p.installed && p.paused),
            find: finder(all),
        };
    }, [res.data]);
    return { ...res, data };
}

/** The space's automations and flows, in the lifecycle's sets: Failing, Blocked
 *  (a tool's schedule something stops), On, Off. A flows read that failed is
 *  `flowsError`, and leaves the automations standing. */
export function useAutomationList(groupId) {
    const res = useLoad(async () => {
        const [a, f] = await Promise.all([
            autosApi.list(groupId),
            autosApi.flows(groupId).then((x) => ({ x }), (e) => ({ e })),
        ]);
        return {
            automations: (a?.automations || []).filter((x) => x?.id).map(automationItem),
            flows: (f.x?.flows || []).filter((x) => x?.id).map(flowItem),
            flowsError: f.e || null,
        };
    }, [groupId]);
    const data = useMemo(() => {
        if (!res.data) return null;
        const list = res.data.automations;
        const is = (states) => (a) => states.includes(a.status.state);
        return {
            automations: list,
            flows: res.data.flows,
            flowsError: res.data.flowsError,
            failing: list.filter(is(['failing'])),
            blocked: list.filter(is(['paused', 'needs_realm'])),
            on: list.filter(is(['on', 'running'])),
            off: list.filter(is(['off'])),
            find: finder(list),
            findFlow: finder(res.data.flows),
        };
    }, [res.data]);
    return { ...res, data };
}

const IDLE = { runs: [], busy: false, error: null };
/** One page of the run feed and the pages after it (`more()`). `filters`:
 *  { pluginId, status, outcome, startedBy, toolId, limit }. */
export function useRunFeed(groupId, filters = {}) {
    const key = JSON.stringify(filters || {});
    const res = useLoad(() => fetchRunFeed(groupId, filters), [groupId, key], { keep: true });
    const [extra, setExtra] = useState({ key: null, runs: [], busy: false, error: null });
    const here = extra.key === `${groupId}|${key}` ? extra : IDLE;
    const data = useMemo(() => {
        if (!res.data) return null;
        const seen = new Set(res.data.runs.map((r) => r.id));
        return { ...res.data, runs: [...res.data.runs, ...here.runs.filter((r) => !seen.has(r.id))] };
    }, [res.data, here.runs]);
    const more = useCallback(async () => {
        if (!data || data.runs.length >= data.total) return;
        const k = `${groupId}|${key}`;
        setExtra((x) => ({ ...(x.key === k ? x : { runs: [] }), key: k, busy: true, error: null }));
        try {
            const next = await fetchRunFeed(groupId, { ...filters, offset: data.runs.length });
            setExtra((x) => ({ key: k, runs: [...(x.key === k ? x.runs : []), ...next.runs], busy: false, error: null }));
        } catch (e) {
            setExtra((x) => ({ ...x, key: k, busy: false, error: e }));
        }
    }, [data, groupId, key]); // eslint-disable-line react-hooks/exhaustive-deps
    const reload = useCallback(() => { setExtra({ key: null, runs: [], busy: false, error: null }); return res.reload(); }, [res]);
    return { ...res, data, reload, more, moreLoading: here.busy, moreError: here.error };
}

/** One run by its id, with what it made. `data` null once loaded: not one this viewer can see. */
export function useRun(groupId, runId) {
    return useLoad(() => readRun(groupId, runId), [groupId, runId], { enabled: !!runId });
}

/** Runs waiting for you (parked for a yes), of one plugin or all. [] on an older server. */
export function usePluginRequests(groupId, { pluginId = null, status = 'pending', enabled = true } = {}) {
    return useLoad(async () => {
        const r = await requestsApi.list(groupId, { status, pluginId });
        const list = Array.isArray(r) ? r : r?.requests || [];
        return list.filter((x) => x?.id).map(requestItem);
    }, [groupId, pluginId, status], { optional: true, fallback: [], enabled });
}

/** One parked run, with what it would touch. */
export function usePluginRequest(groupId, requestId) {
    return useLoad(async () => {
        const r = await requestsApi.get(groupId, requestId);
        const x = r?.request || r;
        return x?.id ? requestItem(x) : (x?.status ? { id: requestId, status: x.status } : null);
    }, [groupId, requestId], { enabled: !!requestId });
}

/** Who may use a plugin: { verified, grants: [accessItem] }; null on an older server. */
export function usePluginAccess(groupId, pluginId, { enabled = true } = {}) {
    return useLoad(async () => {
        const r = await pluginsApi.access(pluginId, groupId);
        return { verified: !!r?.verified, grants: (r?.grants || []).filter((g) => g?.ep_id).map(accessItem) };
    }, [groupId, pluginId], { optional: true, fallback: null, enabled: enabled && !!pluginId });
}

/** What an uninstall takes and leaves (counts); null on an older server. */
export function useInstallPreview(groupId, pluginId, { enabled = true } = {}) {
    return useLoad(() => pluginsApi.installPreview(pluginId, groupId), [groupId, pluginId], { optional: true, fallback: null, enabled: enabled && !!pluginId });
}

/** A plugin's setup here (fields, realms, schedule, approval), for one tool or the plugin. */
export function useSetupConfig(groupId, pluginId, toolId = null, { enabled = true } = {}) {
    return useLoad(() => pluginsApi.setupConfig(pluginId, groupId, toolId), [groupId, pluginId, toolId], { enabled: enabled && !!pluginId });
}
